import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col, Badge, Collapse } from 'react-bootstrap';
import { FiBarChart2, FiRefreshCw, FiChevronDown, FiChevronRight, FiCalendar, FiMapPin, FiInfo, FiUser, FiEye } from 'react-icons/fi';
import { fetchTripsObservationsRequest } from '../../store/actions/tripsActions';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsObservations.css';

const defaultMeta = {
    total: 0,
    skip: 0,
    limit: 10,
    page: 1,
    total_pages: 0,
};

const getSafe = (obj, path, fallback = '-') => {
    try {
        const value = path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
        return value ?? fallback;
    } catch (error) {
        return fallback;
    }
};

const formatDate = (value) => {
    if (!value) return '-';
    if (value instanceof Date) {
        return value.toLocaleDateString('fr-FR');
    }
    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' });
        }
        return value;
    }
    return value;
};

const EMPTY_OBSERVATIONS_FILTERS = {
    policy_code: '',
    activity_start_date: '',
    trip_day: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
};

const renderBooleanBadge = (value, labels = { true: 'Oui', false: 'Non' }) => {
    if (value === null || value === undefined) {
        return <Badge bg="secondary">N/A</Badge>;
    }
    return value ? <Badge bg="success">{labels.true}</Badge> : <Badge bg="warning" text="dark">{labels.false}</Badge>;
};

const AdminTripsObservations = () => {
    const dispatch = useDispatch();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const observationsState = useSelector((state) => state.trips.observations);
    const navigate = useNavigate();

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('trips-observations', EMPTY_OBSERVATIONS_FILTERS, {
        persistWhen: isAuthenticated,
    });
    const [expandedRows, setExpandedRows] = useState({});

    const loadObservations = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        Object.entries(activeFilters).forEach(([key, value]) => {
            if (value && value.toString().trim() !== '') {
                params[key] = value.toString().trim();
            }
        });

        dispatch(fetchTripsObservationsRequest(params));
    }, [dispatch, itemsPerPage, appliedFilters]);

    useEffect(() => {
        if (isAuthenticated) {
            loadObservations(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    useEffect(() => {
        loadObservations(currentPage);
    }, [currentPage, loadObservations]);

    const observationsData = useMemo(() => {
        const rawData = observationsState?.data?.data || observationsState?.data?.records || observationsState?.data || [];
        return Array.isArray(rawData) ? rawData : [];
    }, [observationsState]);

    const meta = useMemo(() => ({
        ...defaultMeta,
        ...(observationsState?.data?.meta || {}),
    }), [observationsState]);

    const totalPages = meta.total_pages || Math.max(1, Math.ceil((meta.total || 0) / itemsPerPage));

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        loadObservations(1, filters);
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_OBSERVATIONS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadObservations(1, emptyFilters);
        }, 100);
    };

    const handleRefresh = () => {
        loadObservations(currentPage);
    };

    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    const toggleRow = (rowId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    };

    const getRowId = (item, index) => {
        return (
            item?.policy_info?.policy_id ||
            item?.activity_info?.activity_id ||
            item?.last_observation_info?.last_observation_id ||
            item?.client_info?.client_id ||
            `row-${index}`
        );
    };

    return (
        <div className="dashboard-page admin-trips-observations-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiBarChart2 className="me-2" />
                                Trip - Gestion Observations
                            </h2>
                            <p className="text-muted mb-0">Observations météorologiques liées aux séjours</p>
                        </div>
                    </div>

                    {observationsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {observationsState.error}
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            placeholder="Ex: 100983581"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date de début de l&apos;activité</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date"
                                            value={filters.activity_start_date || ''}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Jour d&apos;observation (trip_day)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="trip_day"
                                            value={filters.trip_day || ''}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_first_name"
                                            value={filters.client_first_name}
                                            onChange={handleFilterChange}
                                            placeholder="Prénom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_last_name"
                                            value={filters.client_last_name}
                                            onChange={handleFilterChange}
                                            placeholder="Nom du client"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={observationsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${observationsState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {observationsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des observations (trips)...</p>
                        </div>
                    ) : observationsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{observationsState.error}</Alert>
                        </div>
                    ) : observationsData.length === 0 ? (
                        <div className="text-center py-5">
                            <FiInfo size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune observation disponible avec les critères sélectionnés.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}></th>
                                            <th>Code police</th>
                                            <th>Réservation</th>
                                            <th>Période activité</th>
                                            <th>Station météo</th>
                                            <th>Total pluie (mm)</th>
                                            <th>Observation déclenchée</th>
                                            <th>ID observation</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {observationsData.map((item, index) => {
                                            const rowId = getRowId(item, index);
                                            const isExpanded = !!expandedRows[rowId];
                                            const stationName = getSafe(item, ['last_observation_info', 'last_station_name']);
                                            const stationId = getSafe(item, ['last_observation_info', 'last_station_id']);
                                            const totalPrecip = getSafe(item, ['last_observation_info', 'last_total_mm_prc'], '0');
                                            const isTriggeredObs = getSafe(item, ['CheckClaims', 'is_triggered_observation'], null);

                                            // Si is_triggered_observation est null, on retourne null pour afficher "En attente de traitement"
                                            // Sinon, on retourne true/false selon la valeur
                                            const triggeredObservation = isTriggeredObs === null ? null : isTriggeredObs;
                                            const activityStart = getSafe(item, ['activity_info', 'activity_start_date']);
                                            const activityEnd = getSafe(item, ['activity_info', 'activity_end_date']);
                                            const policyCode = getSafe(item, ['policy_info', 'policy_code']);
                                            const reservationNumber = getSafe(item, ['activity_info', 'reservation_number']);
                                            const observationId = getSafe(item, ['last_observation_info', 'last_observation_id'], 'N/A');

                                            return (
                                                <React.Fragment key={rowId}>
                                                    <tr className="observation-row">
                                                        <td>
                                                            <Button
                                                                variant="link"
                                                                className="toggle-row-btn"
                                                                onClick={() => toggleRow(rowId)}
                                                            >
                                                                {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                                            </Button>
                                                        </td>
                                                        <td>{policyCode}</td>
                                                        <td>{reservationNumber}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <FiCalendar />
                                                                <span>{`${activityStart || '-'} → ${activityEnd || '-'}`}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span>{stationName}</span>
                                                                {stationId && (
                                                                    <small className="text-muted">ID station : {stationId}</small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>{totalPrecip}</td>
                                                        <td>
                                                            {triggeredObservation === null ? (
                                                                <Badge bg="warning">En attente de traitement</Badge>
                                                            ) : (
                                                                renderBooleanBadge(triggeredObservation)
                                                            )}
                                                        </td>
                                                        <td className="text-break">{observationId}</td>
                                                        <td>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                disabled={!observationId || observationId === 'N/A'}
                                                                onClick={() => navigate(`/admin/trips/observations/${observationId}`)}
                                                            >
                                                                <FiEye className="me-1" />
                                                                Voir
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={7} className="p-0">
                                                            <Collapse in={isExpanded}>
                                                                <div className="observation-details">
                                                                    <Row className="mb-3">
                                                                        <Col md={4}>
                                                                            <Card className="detail-card">
                                                                                <Card.Header>
                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                        <FiMapPin />
                                                                                        <span>Station météo</span>
                                                                                    </div>
                                                                                </Card.Header>
                                                                                <Card.Body>
                                                                                    <ul className="detail-list">
                                                                                        <li>
                                                                                            <strong>Station :</strong> {stationName}
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>ID :</strong> {stationId || 'N/A'}
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>Coordonnées :</strong>{' '}
                                                                                            {`${getSafe(item, ['last_observation_info', 'last_latitude_station'], 'N/A')} / ${getSafe(item, ['last_observation_info', 'last_longitude_station'], 'N/A')}`}
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>Source :</strong> {getSafe(item, ['last_observation_info', 'last_data_source'], 'N/A')}
                                                                                        </li>
                                                                                    </ul>
                                                                                </Card.Body>
                                                                            </Card>
                                                                        </Col>
                                                                        <Col md={4}>
                                                                            <Card className="detail-card">
                                                                                <Card.Header>
                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                        <FiInfo />
                                                                                        <span>Observation</span>
                                                                                    </div>
                                                                                </Card.Header>
                                                                                <Card.Body>
                                                                                    <ul className="detail-list">
                                                                                        <li>
                                                                                            <strong>Dernière observation :</strong>{' '}
                                                                                            {formatDate(getSafe(item, ['last_observation_info', 'trip_day']))}
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>Run :</strong>{' '}
                                                                                            {`${getSafe(item, ['last_observation_info', 'last_observation_init_day'], '-')}/${getSafe(item, ['last_observation_info', 'last_observation_init_month'], '-')}/${getSafe(item, ['last_observation_info', 'last_observation_init_year'], '-')}`}
                                                                                            {' '}à {getSafe(item, ['last_observation_info', 'last_observation_run_hour'], '--')}h
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>Dernière modif :</strong>{' '}
                                                                                            {formatDate(getSafe(item, ['last_observation_info', 'last_observation_last_modified_at']))}
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>Modifié par :</strong>{' '}
                                                                                            {getSafe(item, ['last_observation_info', 'last_observation_last_modified_by'], 'N/A')}
                                                                                        </li>
                                                                                    </ul>
                                                                                </Card.Body>
                                                                            </Card>
                                                                        </Col>
                                                                        <Col md={4}>
                                                                            <Card className="detail-card">
                                                                                <Card.Header>
                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                        <FiUser />
                                                                                        <span>Client & Séjour</span>
                                                                                    </div>
                                                                                </Card.Header>
                                                                                <Card.Body>
                                                                                    <ul className="detail-list">
                                                                                        <li>
                                                                                            <strong>Client :</strong>{' '}
                                                                                            {`${getSafe(item, ['client_info', 'first_name'], '')} ${getSafe(item, ['client_info', 'last_name'], '')}`}
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>Email :</strong>{' '}
                                                                                            {getSafe(item, ['client_info', 'email'], 'N/A')}
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>Adresse :</strong>{' '}
                                                                                            {getSafe(item, ['activity_info', 'activity_address'], 'N/A')} - {getSafe(item, ['activity_info', 'activity_city'], '')}
                                                                                        </li>
                                                                                        <li>
                                                                                            <strong>Pays :</strong>{' '}
                                                                                            {getSafe(item, ['activity_info', 'activity_country'], 'N/A')}
                                                                                        </li>
                                                                                    </ul>
                                                                                </Card.Body>
                                                                            </Card>
                                                                        </Col>
                                                                    </Row>
                                                                    <Card className="detail-card mb-3">
                                                                        <Card.Header>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FiBarChart2 />
                                                                                <span>Données horaire de pluie</span>
                                                                            </div>
                                                                        </Card.Header>
                                                                        <Card.Body className="observation-data-wrapper">
                                                                            <Table striped bordered size="sm" responsive>
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>Heure</th>
                                                                                        <th>Précipitations (mm)</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {(getSafe(item, ['last_observation_info', 'last_observation_data'], []) || []).map((row, idx) => (
                                                                                        <tr key={`${row?.time || idx}-${idx}`}>
                                                                                            <td>{formatDate(row?.time)}</td>
                                                                                            <td>{row?.prcp ?? '-'}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </Table>
                                                                        </Card.Body>
                                                                    </Card>
                                                                </div>
                                                            </Collapse>
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                            {`Affichage ${observationsData.length > 0 ? meta.skip + 1 : 0} - ${Math.min(meta.skip + observationsData.length, meta.total || 0)} sur ${meta.total || 0} enregistrements`}
                        </div>
                        <Pagination className="mb-0">
                            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1} />
                            {(() => {
                                const { startPage, endPage } = getPaginationRange();
                                const items = [];
                                for (let page = startPage; page <= endPage; page += 1) {
                                    items.push(
                                        <Pagination.Item
                                            key={page}
                                            active={page === currentPage}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </Pagination.Item>
                                    );
                                }
                                return items;
                            })()}
                            <Pagination.Next onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsObservations;


